import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { voiceApi } from '@/api/services/voice.api';
import { handleMutationError } from '@/lib/handle-mutation-error';
import { voiceKeys } from '../query-keys';
import type { IListBlockedNumbersQuery } from '../types';

/** Org-wide caller blocklist (voice settings page + widget "Black list"). */
export function useBlockedNumbers(params?: IListBlockedNumbersQuery) {
    return useQuery({
        queryKey: voiceKeys.blockedNumbers.list(params),
        queryFn: () => voiceApi.listBlockedNumbers(params),
    });
}

/**
 * Resolve "is this exact number blocked?" (widget History detail view).
 * There is no dedicated check endpoint — the list's `search` is a substring
 * match, so filter to the exact E.164 client-side. Keyed under the
 * blockedNumbers family so block/unblock invalidations refresh it.
 */
export function useBlockedNumberByPhone(phone: string, enabled = true) {
    return useQuery({
        queryKey: [...voiceKeys.blockedNumbers.all, 'by-phone', phone],
        queryFn: async () => {
            const { data } = await voiceApi.listBlockedNumbers({ search: phone, limit: 5 });
            return data.find((row) => row.phone === phone) ?? null;
        },
        enabled: enabled && Boolean(phone),
        staleTime: 30_000,
    });
}

export function useBlockNumber() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: voiceApi.blockNumber,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: voiceKeys.blockedNumbers.all });
            toast.success('Number blocked');
        },
        onError: (error: unknown) => handleMutationError(error, 'Failed to block number'),
    });
}

export function useUpdateBlockedNumber() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: voiceApi.updateBlockedNumber,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: voiceKeys.blockedNumbers.all });
            toast.success('Blocked number updated');
        },
        onError: (error: unknown) => handleMutationError(error, 'Failed to update blocked number'),
    });
}

export function useUnblockNumber() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: voiceApi.unblockNumber,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: voiceKeys.blockedNumbers.all });
            toast.success('Number unblocked');
        },
        onError: (error: unknown) => handleMutationError(error, 'Failed to unblock number'),
    });
}
